import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Users, BookOpen, Sparkles, FileBadge, Gamepad2,
    Route as RouteIcon, Bell as BellIcon, Layers, Award, SlidersHorizontal,
    MessageSquareText, Palette, BarChart3, LogOut, Menu, ChevronRight,
    Search, Command, FileText, ListChecks, Activity, FlaskConical, Trophy, GraduationCap, Headphones,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { BrandLogo, BRAND } from '@/lib/brand';
import CommandPalette from '@/components/CommandPalette';

const NAV_SECTIONS = [
    {
        label: 'Overview',
        items: [
            { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, testid: 'nav-admin-dashboard' },
            { to: '/admin/reports', label: 'Reports', icon: BarChart3, testid: 'nav-admin-reports' },
            { to: '/admin/ai-analytics', label: 'AI Analytics', icon: MessageSquareText, testid: 'nav-admin-ai-analytics', highlight: 'AI' },
        ],
    },
    {
        label: 'Learning',
        items: [
            { to: '/admin/videos', label: 'Courses', icon: BookOpen, testid: 'nav-admin-courses' },
            { to: '/admin/quiz', label: 'Quiz', icon: ListChecks, testid: 'nav-admin-quiz' },
            { to: '/admin/pathways', label: 'Pathways', icon: RouteIcon, testid: 'nav-admin-pathways' },
            { to: '/admin/stories', label: 'Stories', icon: Sparkles, testid: 'nav-admin-stories' },
            { to: '/admin/certificates', label: 'Certificates', icon: FileBadge, testid: 'nav-admin-certificates' },
            { to: '/admin/offline-training', label: 'Offline Training', icon: GraduationCap, testid: 'nav-admin-offline-training', highlight: 'NEW' },
        ],
    },
    {
        label: 'Engagement',
        items: [
            { to: '/admin/games', label: 'Manage Games', icon: Gamepad2, testid: 'nav-admin-games' },
            { to: '/admin/contest', label: 'Contest Leaderboards', icon: Trophy, testid: 'nav-admin-contest', highlight: 'NEW' },
            { to: '/admin/arcade-questions', label: 'Game Questions', icon: ListChecks, testid: 'nav-admin-arcade-questions', highlight: 'NEW' },
            { to: '/admin/badges', label: 'Badges', icon: Award, testid: 'nav-admin-badges' },
            { to: '/admin/notifications', label: 'Notifications', icon: BellIcon, testid: 'nav-admin-notifications', highlight: 'NEW' },
            { to: '/admin/cohorts', label: 'Cohorts', icon: Layers, testid: 'nav-admin-cohorts', highlight: 'NEW' },
        ],
    },
    {
        label: 'Configuration',
        items: [
            { to: '/admin/users', label: 'Users & Roles', icon: Users, testid: 'nav-admin-users' },
            { to: '/admin/callcenter', label: 'Call Centre Logins', icon: Headphones, testid: 'nav-admin-callcenter', highlight: 'NEW' },
            { to: '/admin/taxonomies', label: 'Course Settings', icon: SlidersHorizontal, testid: 'nav-admin-taxonomies' },
            { to: '/admin/brand', label: 'Brand Settings', icon: Palette, testid: 'nav-admin-brand' },
            { to: '/admin/loyalty-monitor', label: 'Loyalty API Monitor', icon: Activity, testid: 'nav-admin-loyalty-monitor', highlight: 'NEW' },
            { to: '/admin/uat', label: 'UAT Test Cases', icon: FlaskConical, testid: 'nav-admin-uat', highlight: 'UAT' },
            { to: '/admin/docs', label: 'Documentation', icon: FileText, testid: 'nav-admin-docs' },
        ],
    },
];

const ALL_NAV = NAV_SECTIONS.flatMap((s) => s.items);

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [cmdOpen, setCmdOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setCmdOpen(true);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const onLogout = () => { logout(); navigate('/'); };

    const currentItem = ALL_NAV.find((n) => n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to));
    const breadcrumb = currentItem?.label || 'Command Center';

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-40 w-72 transform transition-transform lg:translate-x-0 lg:static lg:flex
                    ${open ? 'translate-x-0' : '-translate-x-full'}`}
                style={{ background: BRAND.colors.sidebar }}
                data-testid="admin-sidebar"
            >
                <div className="flex flex-col w-full h-screen lg:h-auto lg:min-h-screen">
                    <div className="px-5 py-5 border-b border-white/5">
                        <BrandLogo dark />
                    </div>

                    {/* Quick command */}
                    <div className="px-3 pt-4">
                        <button
                            onClick={() => setCmdOpen(true)}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-xs transition border border-white/5"
                            data-testid="sidebar-cmd-btn"
                        >
                            <Search className="h-3.5 w-3.5" />
                            <span className="flex-1 text-left">Search & jump…</span>
                            <kbd className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-white/10 font-mono">
                                <Command className="h-2.5 w-2.5" />K
                            </kbd>
                        </button>
                    </div>

                    <nav className="flex-1 px-3 py-4 overflow-y-auto no-scrollbar">
                        {NAV_SECTIONS.map((section) => (
                            <div key={section.label} className="mb-5">
                                <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{section.label}</div>
                                {section.items.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.exact}
                                        onClick={() => setOpen(false)}
                                        data-testid={item.testid}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2 rounded-xl mb-0.5 text-sm font-medium transition relative
                                            ${isActive
                                                ? 'bg-brand-yellow text-slate-900 shadow-md'
                                                : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <item.icon className="h-4 w-4" />
                                                <span className="flex-1">{item.label}</span>
                                                {item.highlight && (
                                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-slate-900 text-brand-yellow' : 'bg-gradient-to-r from-brand-orange to-brand-red text-white'}`}>
                                                        {item.highlight}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                        ))}
                    </nav>

                    <div className="px-3 py-3 border-t border-white/5">
                        <div className="px-3 py-2.5 rounded-xl bg-white/5 flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-brand-yellow text-slate-900 flex items-center justify-center font-display font-extrabold text-sm">
                                {(user?.full_name || 'A').charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-white font-semibold truncate" data-testid="admin-user-name">
                                    {user?.full_name}
                                </div>
                                <div className="text-[10px] text-slate-400 capitalize truncate">{user?.role?.replaceAll('_', ' ')}</div>
                            </div>
                        </div>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-btn"
                            className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </aside>

            {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

            <main className="flex-1 min-w-0 flex flex-col" data-testid="admin-content">
                <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200 px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="admin-sidebar-toggle">
                            <Menu className="h-5 w-5" />
                        </button>
                        <nav className="hidden sm:flex items-center text-sm text-slate-500 gap-1.5 min-w-0">
                            <Link to="/admin" className="hover:text-slate-900">Admin</Link>
                            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="text-slate-900 font-semibold truncate" data-testid="admin-breadcrumb">{breadcrumb}</span>
                        </nav>
                    </div>
                    <button
                        onClick={() => setCmdOpen(true)}
                        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 text-slate-500 text-xs"
                        data-testid="header-cmd-btn"
                    >
                        <Search className="h-3 w-3" />
                        <span>Quick search</span>
                        <kbd className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 font-mono">
                            <Command className="h-2.5 w-2.5" />K
                        </kbd>
                    </button>
                    <Link to="/learn/arcade" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-br from-brand-yellow to-amber-400 text-slate-900 text-xs font-extrabold hover:brightness-95 shadow" data-testid="admin-open-arcade">
                        <Gamepad2 className="h-3.5 w-3.5" /> Play Arcade
                    </Link>
                    <Link to="/learn" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50" data-testid="admin-switch-trainee">
                        Trainee View
                    </Link>
                    <button className="p-2 rounded-lg hover:bg-slate-100 relative" data-testid="admin-notifications">
                        <BellIcon className="h-4 w-4 text-slate-600" />
                    </button>
                </header>
                <div className="p-4 lg:p-8 flex-1 min-w-0">
                    <Outlet />
                </div>
            </main>

            <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
        </div>
    );
}
